import { Injectable } from '@angular/core';
import { EmitterService } from '../../../shared/services/emitter.service';
import {
  BaseTreeNode,
  isSectionNode,
  isSubsectionFileNode,
  isSubsectionOptionNode,
  isSubsectionRootNode,
  isSubsectionTopicNode,
  isSubsectionVersionNode,
  SectionNode,
  SubsectionFileNode,
  SubsectionOptionNode,
  SubsectionRootNode,
  SubsectionTopicNode,
  SubsectionVersionNode,
  TreeNodeType,
  BaseNavigationResult,
  NavigationResult,
  SubsectionTreeState,
} from '../../models';
import {
  FileInfo,
  OptionInfo,
  OptionVersion,
  OptionVersionSimple,
  ProjectSubsection,
  TopicInfo,
} from '../../../shared/interfaces';

type OptionVersionLike = OptionVersion | OptionVersionSimple;

@Injectable({
  providedIn: 'root',
})
export class SubsectionTreeNavigationService {
  public getHomeNavigationParams(state: SubsectionTreeState): NavigationResult | void {
    if (state.selectedNode && !isSubsectionRootNode(state.selectedNode)) {
      return this.getNodeNavigationParams(state.root);
    }
    EmitterService.get('SUBSECTION_NAVIGATION:DASHBOARD').emit();
  }

  public getTopicNavigationParams(topicNode: SubsectionTopicNode): NavigationResult {
    if (!isSubsectionTopicNode(topicNode)) {
      return { type: 'direct', params: null };
    }
    const topicData: TopicInfo = topicNode.data;
    const navigationParams: BaseNavigationResult = this.createBaseNavigationParams({
      projectId: topicData.project,
      sectionId: topicData.section,
      subsectionId: topicData.subsection,
      topicId: topicData.topicId,
    });
    return { type: 'direct', params: navigationParams };
  }

  public getSubsectionNavigationParams(subsectionNode: SubsectionRootNode): NavigationResult {
    if (!isSubsectionRootNode(subsectionNode)) {
      return { type: 'direct', params: null };
    }
    const subsectionData: ProjectSubsection = subsectionNode.data;
    const navigationParams: BaseNavigationResult = this.createBaseNavigationParams({
      projectId: subsectionNode.projectId,
      sectionId: subsectionData.projectSectionId,
      subsectionId: subsectionData.id,
    });
    return { type: 'direct', params: navigationParams };
  }

  public getOptionNavigationParams(optionNode: SubsectionOptionNode): NavigationResult {
    if (!isSubsectionOptionNode(optionNode)) {
      return { type: 'direct', params: null };
    }
    const optionData: OptionInfo = optionNode.data;
    const navigationParams: BaseNavigationResult = this.createBaseNavigationParams({
      projectId: optionData.project,
      sectionId: optionData.section,
      subsectionId: optionData.subsection,
      topicId: optionData.topicId,
      optionId: optionData.optionId,
    });
    return { type: 'direct', params: navigationParams };
  }

  public getOptionNavigationWithPendingFile(optionNode: SubsectionOptionNode): NavigationResult {
    if (!isSubsectionOptionNode(optionNode)) {
      return { type: 'direct', params: null };
    }
    const optionData: OptionInfo = optionNode.data;
    return {
      params: this.getOptionNavigationParams(optionNode).params,
      type: 'version-dependent',
      pendingFileNavigation: {
        projectId: optionData.project,
        sectionId: optionData.section,
        subsectionId: optionData.subsection,
        topicId: optionData.topicId,
        optionId: optionData.optionId,
      },
    };
  }

  public getVersionNavigationParams(versionNode: SubsectionVersionNode): NavigationResult {
    if (!isSubsectionVersionNode(versionNode)) {
      return { type: 'direct', params: null };
    }
    const versionData: OptionVersionLike | null = versionNode.data ?? null;
    const option = this.getParentNode<SubsectionOptionNode>(TreeNodeType.Option, versionNode);
    const optionData: OptionInfo | null = option?.data ?? null;
    const navigationParams: BaseNavigationResult = this.createBaseNavigationParams({
      projectId: optionData?.project ?? null,
      sectionId: optionData?.section ?? null,
      subsectionId: optionData?.subsection ?? null,
      topicId: optionData?.topicId ?? null,
      optionId: optionData?.optionId ?? null,
      versionNumber: versionData?.versionNumber ?? null,
    });
    return { type: 'direct', params: navigationParams };
  }

  public getVersionNavigationWithPendingFile(versionNode: SubsectionVersionNode): NavigationResult {
    if (!isSubsectionVersionNode(versionNode)) {
      return { type: 'direct', params: null };
    }
    const versionData: OptionVersionLike | null = versionNode.data ?? null;
    const option = this.getParentNode<SubsectionOptionNode>(TreeNodeType.Option, versionNode);
    const optionData: OptionInfo | null = option?.data ?? null;
    const topic = this.getParentNode<SubsectionTopicNode>(TreeNodeType.Topic, option);
    const topicData: TopicInfo | null = topic?.data ?? null;
    return {
      params: this.getVersionNavigationParams(versionNode).params,
      type: 'version-dependent',
      pendingFileNavigation: {
        projectId: optionData?.project ?? null,
        sectionId: optionData?.section ?? null,
        subsectionId: optionData?.subsection ?? null,
        topicId: optionData?.topicId ?? topicData?.topicId ?? null,
        optionId: optionData?.optionId ?? null,
        versionNumber: versionData?.versionNumber ?? null,
      },
    };
  }

  public getFileNavigationParams(fileNode: SubsectionFileNode): NavigationResult {
    if (!isSubsectionFileNode(fileNode)) {
      return { type: 'direct', params: null };
    }
    const fileData: FileInfo = fileNode.data;
    const option = this.getParentNode<SubsectionOptionNode>(TreeNodeType.Option, fileNode);
    const optionData: OptionInfo | null = option?.data ?? null;
    const version = this.getParentNode<SubsectionVersionNode>(TreeNodeType.Version, fileNode);
    const versionData: OptionVersionLike | null = version?.data ?? null;
    const navigationParams: BaseNavigationResult = this.createBaseNavigationParams({
      projectId: optionData?.project ?? null,
      sectionId: optionData?.section ?? null,
      subsectionId: optionData?.subsection ?? null,
      topicId: optionData?.topicId ?? null,
      optionId: optionData?.optionId ?? null,
      versionNumber: versionData?.versionNumber ?? null,
      fileId: fileData?.id ?? null,
    });
    return { type: 'direct', params: navigationParams };
  }

  public getSectionNavigationParams(sectionNode: SectionNode): NavigationResult {
    const navigationParams: BaseNavigationResult = this.createBaseNavigationParams({
      projectId: sectionNode.parent?.id ?? null,
      sectionId: sectionNode.id,
      subsectionId: sectionNode.children?.[0]?.id ?? null,
    });
    return { type: 'direct', params: navigationParams };
  }

  public getNodeNavigationParams(node: BaseTreeNode | null): NavigationResult | null {
    if (!node) {
      return null;
    }
    if (isSectionNode(node)) {
      return this.getSectionNavigationParams(node);
    }
    if (isSubsectionRootNode(node)) {
      return this.getSubsectionNavigationParams(node);
    }
    if (isSubsectionTopicNode(node)) {
      return this.getTopicNavigationParams(node);
    }
    if (isSubsectionOptionNode(node)) {
      return this.getOptionNavigationParams(node);
    }
    if (isSubsectionVersionNode(node)) {
      return this.getVersionNavigationParams(node);
    }
    if (isSubsectionFileNode(node)) {
      return this.getFileNavigationParams(node);
    }
    return null;
  }

  public getUpItemNavigationParams(state: SubsectionTreeState): NavigationResult | null {
    const { selectedNode } = state;
    if (!selectedNode?.parent) {
      return null;
    }
    if (isSubsectionVersionNode(selectedNode.parent) && selectedNode.parent.parent?.children.length === 1) {
      return this.getNodeNavigationParams(selectedNode.parent.parent);
    }
    if (isSubsectionOptionNode(selectedNode.parent) && selectedNode.parent.children.length === 1) {
      return this.getNodeNavigationParams(selectedNode.parent.parent);
    }
    return this.getNodeNavigationParams(selectedNode.parent);
  }

  public getDownItemNavigationParams(state: SubsectionTreeState): NavigationResult | null {
    const { selectedNode } = state;
    if (!selectedNode?.children?.length) {
      return null;
    }
    return this.getNodeNavigationParams(selectedNode.children[0]);
  }

  public getNextItemNavigationParams(state: SubsectionTreeState): NavigationResult | null {
    const { selectedNode, root } = state;
    if (!selectedNode) {
      return this.getFirstTopicNavigation(root as SubsectionRootNode);
    }
    if (isSubsectionFileNode(selectedNode)) {
      return this.getNextFromFile(selectedNode);
    }
    if (isSubsectionVersionNode(selectedNode)) {
      return this.getNextFromVersion(selectedNode);
    }
    if (isSubsectionOptionNode(selectedNode)) {
      return this.getNextFromOption(selectedNode);
    }
    if (isSubsectionTopicNode(selectedNode)) {
      return this.getNextFromTopic(selectedNode);
    }
    if (isSubsectionRootNode(selectedNode)) {
      return this.getFirstTopicNavigation(selectedNode);
    }
    return null;
  }

  public getPreviousItemNavigationParams(state: SubsectionTreeState): NavigationResult | null {
    const { selectedNode } = state;
    if (!selectedNode) {
      return null;
    }
    if (isSubsectionFileNode(selectedNode)) {
      return this.getPreviousFromFile(selectedNode);
    }
    if (isSubsectionVersionNode(selectedNode)) {
      return this.getPreviousFromVersion(selectedNode);
    }
    if (isSubsectionOptionNode(selectedNode)) {
      return this.getPreviousFromOption(selectedNode);
    }
    if (isSubsectionTopicNode(selectedNode)) {
      return this.getPreviousFromTopic(selectedNode);
    }
    if (isSubsectionRootNode(selectedNode) && selectedNode.children?.length) {
      return this.getTopicNavigationParams(selectedNode.children[0] as SubsectionTopicNode);
    }
    return null;
  }

  private getFirstTopicNavigation(root: BaseTreeNode | null): NavigationResult | null {
    if (!root || !isSubsectionRootNode(root) || !root.children?.length) {
      return null;
    }
    return this.getTopicNavigationParams(root.children[0] as SubsectionTopicNode);
  }

  private getNextFromFile(fileNode: SubsectionFileNode): NavigationResult | null {
    const versionNode: SubsectionVersionNode | null = this.getParentNode<SubsectionVersionNode>(
      TreeNodeType.Version,
      fileNode
    );
    const optionNode: SubsectionOptionNode | null = this.getParentNode<SubsectionOptionNode>(
      TreeNodeType.Option,
      fileNode
    );
    const topicNode: SubsectionTopicNode | null = this.getParentNode<SubsectionTopicNode>(TreeNodeType.Topic, fileNode);
    if (!versionNode || !optionNode || !topicNode) {
      return null;
    }
    const fileIndex: number = versionNode.children.indexOf(fileNode);
    if (fileIndex < versionNode.children.length - 1) {
      return this.getFileNavigationParams(versionNode.children[fileIndex + 1] as SubsectionFileNode);
    }
    const versionIndex: number = optionNode.children.indexOf(versionNode);
    if (versionIndex < optionNode.children.length - 1) {
      return this.getVersionNavigationParams(optionNode.children[versionIndex + 1] as SubsectionVersionNode);
    }
    const optionIndex: number = topicNode.children.indexOf(optionNode);
    if (optionIndex < topicNode.children.length - 1) {
      const nextOption: SubsectionOptionNode = topicNode.children[optionIndex + 1] as SubsectionOptionNode;
      if (nextOption.children.length === 1) {
        return this.getVersionNavigationParams(nextOption.children[0] as SubsectionVersionNode);
      }
      return this.getOptionNavigationParams(nextOption);
    }
    const rootNode: SubsectionRootNode | null = this.getParentNode<SubsectionRootNode>(
      TreeNodeType.SubsectionRoot,
      topicNode
    );
    if (!rootNode) {
      return null;
    }
    const topicIndex: number = rootNode.children.indexOf(topicNode);
    if (topicIndex < rootNode.children.length - 1) {
      return this.getTopicNavigationParams(rootNode.children[topicIndex + 1] as SubsectionTopicNode);
    }
    return null;
  }

  private getNextFromVersion(versionNode: SubsectionVersionNode): NavigationResult | null {
    const optionNode: SubsectionOptionNode | null = this.getParentNode<SubsectionOptionNode>(
      TreeNodeType.Option,
      versionNode
    );
    const topicNode: SubsectionTopicNode | null = this.getParentNode<SubsectionTopicNode>(
      TreeNodeType.Topic,
      versionNode
    );
    const rootNode: SubsectionRootNode | null = this.getParentNode<SubsectionRootNode>(
      TreeNodeType.SubsectionRoot,
      topicNode
    );
    if (!optionNode || !topicNode || !rootNode) {
      return null;
    }
    if (versionNode.children.length > 0) {
      return this.getFileNavigationParams(versionNode.children[0] as SubsectionFileNode);
    }
    const versionIndex: number = optionNode.children.indexOf(versionNode);
    if (versionIndex < optionNode.children.length - 1) {
      return this.getVersionNavigationParams(optionNode.children[versionIndex + 1] as SubsectionVersionNode);
    }
    const optionIndex: number = topicNode.children.indexOf(optionNode);
    if (optionIndex < topicNode.children.length - 1) {
      return this.getOptionNavigationParams(topicNode.children[optionIndex + 1] as SubsectionOptionNode);
    }
    const topicIndex: number = rootNode.children.indexOf(topicNode);
    if (topicIndex < rootNode.children.length - 1) {
      return this.getTopicNavigationParams(rootNode.children[topicIndex + 1] as SubsectionTopicNode);
    }
    return null;
  }

  private getNextFromOption(optionNode: SubsectionOptionNode): NavigationResult | null {
    const topicNode: SubsectionTopicNode | null = this.getParentNode<SubsectionTopicNode>(
      TreeNodeType.Topic,
      optionNode
    );
    if (!topicNode) {
      return null;
    }
    if (optionNode.children.length === 1) {
      const versionNode: SubsectionVersionNode = optionNode.children[0] as SubsectionVersionNode;
      if (versionNode.children.length > 0) {
        return this.getFileNavigationParams(versionNode.children[0] as SubsectionFileNode);
      }
      return this.getVersionNavigationParams(versionNode);
    }
    if (optionNode.children.length > 1) {
      return this.getVersionNavigationParams(optionNode.children[0] as SubsectionVersionNode);
    }
    const optionIndex: number = topicNode.children.indexOf(optionNode);
    if (optionIndex < topicNode.children.length - 1) {
      return this.getOptionNavigationParams(topicNode.children[optionIndex + 1] as SubsectionOptionNode);
    }
    const rootNode: BaseTreeNode | null = this.getParentNode<SubsectionRootNode>(
      TreeNodeType.SubsectionRoot,
      topicNode
    );
    if (!rootNode) {
      return null;
    }
    const topicIndex: number = rootNode.children.indexOf(topicNode);
    if (topicIndex < rootNode.children.length - 1) {
      return this.getTopicNavigationParams(rootNode.children[topicIndex + 1] as SubsectionTopicNode);
    }
    return null;
  }

  private getNextFromTopic(topicNode: SubsectionTopicNode): NavigationResult | null {
    if (topicNode.children.length > 0) {
      const firstOption: SubsectionOptionNode = topicNode.children[0] as SubsectionOptionNode;
      if (firstOption.children.length > 0) {
        return this.getVersionNavigationParams(firstOption.children[0] as SubsectionVersionNode);
      }
      return this.getOptionNavigationParams(firstOption);
    }
    const rootNode: BaseTreeNode | null = this.getParentNode<SubsectionRootNode>(
      TreeNodeType.SubsectionRoot,
      topicNode
    );
    if (!rootNode) {
      return null;
    }
    const topicIndex: number = rootNode.children.indexOf(topicNode);
    if (topicIndex < rootNode.children.length - 1) {
      return this.getTopicNavigationParams(rootNode.children[topicIndex + 1] as SubsectionTopicNode);
    }
    return null;
  }

  private getPreviousFromFile(fileNode: SubsectionFileNode): NavigationResult | null {
    const versionNode: SubsectionVersionNode | null = this.getParentNode<SubsectionVersionNode>(
      TreeNodeType.Version,
      fileNode
    );
    if (!versionNode) {
      return null;
    }
    const fileIndex: number = versionNode.children.indexOf(fileNode);
    if (fileIndex > 0) {
      return this.getFileNavigationParams(versionNode.children[fileIndex - 1] as SubsectionFileNode);
    }
    return this.getVersionNavigationParams(versionNode);
  }

  private getPreviousFromVersion(versionNode: SubsectionVersionNode): NavigationResult | null {
    const optionNode: SubsectionOptionNode | null = this.getParentNode<SubsectionOptionNode>(
      TreeNodeType.Option,
      versionNode
    );
    const topicNode: SubsectionTopicNode | null = this.getParentNode<SubsectionTopicNode>(
      TreeNodeType.Topic,
      versionNode
    );
    if (!optionNode || !topicNode) {
      return null;
    }
    const versionIndex: number = optionNode.children.indexOf(versionNode);
    if (versionIndex > 0) {
      const previousVersion: SubsectionVersionNode = optionNode.children[versionIndex - 1] as SubsectionVersionNode;
      if (previousVersion.children.length > 0) {
        return this.getFileNavigationParams(
          previousVersion.children[previousVersion.children.length - 1] as SubsectionFileNode
        );
      }
      return this.getVersionNavigationParams(previousVersion);
    }
    const optionIndex: number = topicNode.children.indexOf(optionNode);
    if (optionIndex > 0) {
      const previousOption: SubsectionOptionNode = topicNode.children[optionIndex - 1] as SubsectionOptionNode;
      if (previousOption.children.length > 0) {
        const lastVersion: SubsectionVersionNode = previousOption.children[
          previousOption.children.length - 1
        ] as SubsectionVersionNode;
        if (lastVersion.children.length > 0) {
          return this.getFileNavigationParams(
            lastVersion.children[lastVersion.children.length - 1] as SubsectionFileNode
          );
        }
        return this.getVersionNavigationWithPendingFile(lastVersion);
      }
      return this.getOptionNavigationParams(previousOption);
    }
    return this.getTopicNavigationParams(topicNode);
  }

  private getPreviousFromOption(optionNode: SubsectionOptionNode): NavigationResult | null {
    const topicNode = this.getParentNode<SubsectionTopicNode>(TreeNodeType.Topic, optionNode);
    if (!topicNode) {
      return null;
    }
    const optionIndex: number = topicNode.children.indexOf(optionNode);
    if (optionIndex > 0) {
      const previousOption: SubsectionOptionNode = topicNode.children[optionIndex - 1] as SubsectionOptionNode;
      if (previousOption.children.length === 1) {
        const versionNode: SubsectionVersionNode = previousOption.children[0] as SubsectionVersionNode;
        return this.getVersionNavigationWithPendingFile(versionNode);
      }
      if (previousOption.children.length > 1) {
        const lastVersion: SubsectionVersionNode = previousOption.children[
          previousOption.children.length - 1
        ] as SubsectionVersionNode;
        return this.getVersionNavigationParams(lastVersion);
      }
      return this.getOptionNavigationParams(previousOption);
    }
    return this.getTopicNavigationParams(topicNode);
  }

  private getPreviousFromTopic(topicNode: SubsectionTopicNode): NavigationResult | null {
    const rootNode: BaseTreeNode | null = this.getParentNode<SubsectionRootNode>(
      TreeNodeType.SubsectionRoot,
      topicNode
    );
    if (!rootNode) {
      return null;
    }
    const topicIndex: number = rootNode.children.indexOf(topicNode);
    if (topicIndex > 0) {
      const previousTopic: SubsectionTopicNode = rootNode.children[topicIndex - 1] as SubsectionTopicNode;
      if (previousTopic.children.length > 0) {
        const lastOption: SubsectionOptionNode = previousTopic.children[
          previousTopic.children.length - 1
        ] as SubsectionOptionNode;
        if (lastOption.children.length > 0) {
          const lastVersion: SubsectionVersionNode = lastOption.children[
            lastOption.children.length - 1
          ] as SubsectionVersionNode;
          return this.getVersionNavigationWithPendingFile(lastVersion);
        }
        return this.getOptionNavigationParams(lastOption);
      }
      return this.getTopicNavigationParams(previousTopic);
    }
    return this.getNodeNavigationParams(rootNode);
  }

  private getParentNode<TNode extends BaseTreeNode>(targetType: TreeNodeType, node: BaseTreeNode | null): TNode | null {
    let currentNode: BaseTreeNode | null = node?.parent ?? null;
    while (currentNode) {
      if (currentNode.type === targetType) {
        return currentNode as TNode;
      }
      currentNode = currentNode.parent;
    }
    return null;
  }

  private createBaseNavigationParams(params: Partial<BaseNavigationResult> = {}): BaseNavigationResult {
    const defaultParams: BaseNavigationResult = {
      projectId: null,
      sectionId: null,
      subsectionId: null,
      topicId: null,
      optionId: null,
      versionNumber: null,
      fileId: null,
    };
    return { ...defaultParams, ...params };
  }
}
